import { notifyCompanyRelationshipsChanged } from "../../utils/companyRelationshipCounts";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DeleteOutline as DeleteOutlineIcon } from "../icons/FigmaControlIcons";

import SharedBulkActionsBar from "../BulkActionsBar";
import GenericDataTable from "../GenericDataTable";
import AddToSequenceDialog from "../PopupWindows/AddToSequenceDialog.jsx";
import { buildOpenCompanyNavEntity } from "../../utils/companyNavigation";
import { buildJobSequenceItem } from "../../utils/sequenceItems";
import { getDistinctJobHiringManagerName } from "../../utils/recruiterOwnership";
import { toolbarPillButtonSx } from "../../utils/pillButtonStyles";
import {
  CompanyTabSectionHeader,
  TAB_BLUE,
  TAB_PURPLE,
} from "./CompanyTabHeaderShared";
import { TAB_VIEW_CONTENT_WRAPPER_SX } from "../tabViewContentSpacing";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const TABLE_SELECTION_COLUMN_WIDTH_PX = 44;
const TABLE_SELECTION_COLUMN_INSET_PX = 22;
// GenericDataTable header cells already include 16px horizontal padding.
const TABLE_SELECTION_HEADER_INSET_PX = 6;
const TABLE_SELECTION_CHECKBOX_SIZE_PX = 18;

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        "x-access-token": token.replace(/^Bearer\s+/i, ""),
      }
    : {};
};

const JOB_STATUS_OPTIONS = [
  "New",
  "Accepting Candidates",
  "Placed",
  "On hold",
  "For Review",
  "Closed",
  "Pending",
  "Open",
  "Approved",
  "Rejected",
  "Completed",
  "Scheduled",
  "Archived",
  "In Progress",
];

const statusChipSx = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "new") return { bgcolor: "#EEF4FF", color: "#2970FF" };
  if (normalized === "accepting candidates") return { bgcolor: "#FFF3D6", color: "#F79009" };
  if (normalized === "placed") return { bgcolor: "#FFF3D6", color: "#F79009" };
  if (normalized === "closed") return { bgcolor: "#FCE7F3", color: "#D92D9C" };
  if (normalized === "on hold") return { bgcolor: "#ECFDF3", color: "#12B76A" };
  if (normalized === "for review") return { bgcolor: "#FFF3D6", color: "#F79009" };
  if (normalized === "open") return { bgcolor: "#ECFDF3", color: "#12B76A" };
  if (normalized === "pending") return { bgcolor: "#F5F3FF", color: "#7C3AED" };
  if (normalized === "approved") return { bgcolor: "#DCFCE7", color: "#15803D" };
  if (normalized === "rejected") return { bgcolor: "#FEE2E2", color: "#B91C1C" };
  if (normalized === "in progress") return { bgcolor: "#EFF6FF", color: "#1D4ED8" };
  return { bgcolor: "#F3F4F6", color: "#374151" };
};

function RowKebab({ onDelete, onMenuOpen, onMenuClose }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    onMenuClose?.();
  }, [onMenuClose]);

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
          onMenuOpen?.();
        }}
      >
        <MoreVertIcon sx={{ fontSize: 18, color: "#6B7280" }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={(event) => event.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
            minWidth: 160,
          },
        }}
      >
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            closeMenu();
            onDelete?.();
          }}
          sx={{ gap: 1.25, color: "#B91C1C" }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

const BulkActionsBar = ({
  selectedCount,
  onAddToSequence,
  onBulkDelete,
  onChangeStatus,
  statusFeedback = null,
  onStatusFeedbackClose,
}) => (
  <SharedBulkActionsBar
    selectedCount={selectedCount}
    selectionLabel="job"
    actions={[
      {
        type: "button",
        label: "Add to Sequence",
        onClick: onAddToSequence,
      },
      { type: "divider" },
      { type: "button", label: "Assign Team Member" },
      { type: "divider" },
      {
        type: "menu",
        label: "Change Status",
        options: JOB_STATUS_OPTIONS,
        onSelect: onChangeStatus,
        feedback: statusFeedback,
        onFeedbackClose: onStatusFeedbackClose,
        disabledMessage:
          selectedCount === 0 ? "Please select an item to change status." : "",
        disabled: selectedCount === 0,
      },
      { type: "divider" },
      { type: "button", label: "Export" },
      { type: "divider" },
      {
        type: "button",
        label: "Delete",
        onClick: onBulkDelete,
        disabled: selectedCount === 0,
      },
    ]}
    rightButton={{
      label: "Match Candidates",
      background: "#8B5CF6",
      sx: toolbarPillButtonSx,
    }}
    sx={{
      py: 1.75,
      px: 1.5,
      borderBottom: "1px solid #E5E7EB",
      backgroundColor: "#fff",
    }}
  />
);

const CHIP_COLORS = {
  keywords: { bgcolor: "#EAF2FF", color: TAB_BLUE, borderColor: "#C7DBFF" },
  skills: { bgcolor: "#EAF2FF", color: TAB_BLUE, borderColor: "#C7DBFF" },
};

function PreferencesSection({ company = {} }) {
  const parseChips = (value) => {
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
      } catch {
        return value.split(",").map((item) => item.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const keywordTags = parseChips(company.keywords_tags ?? company.keyword_tags ?? company.tags);
  const candidateSkills = parseChips(
    company.candidate_skills ?? company.skills ?? company.required_skills
  );

  if (!keywordTags.length && !candidateSkills.length) return null;

  return (
    <Box mt={4}>
      <CompanyTabSectionHeader title="Preferences" actions={[{ label: "Edit" }]} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}>
            Keywords Tags:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {keywordTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => {}}
                sx={{
                  height: 24,
                  borderRadius: "999px",
                  fontSize: 11,
                  fontWeight: 500,
                  ...CHIP_COLORS.keywords,
                  border: `1px solid ${CHIP_COLORS.keywords.borderColor}`,
                  "& .MuiChip-deleteIcon": {
                    fontSize: 14,
                    color: CHIP_COLORS.keywords.color,
                  },
                }}
              />
            ))}
          </Stack>
          <Typography
            component="button"
            type="button"
            sx={{
              mt: 1.2,
              border: 0,
              background: "transparent",
              p: 0,
              fontSize: 12,
              color: TAB_PURPLE,
              cursor: "pointer",
            }}
          >
            Add New
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}>
            Candidate Skills:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {candidateSkills.map((skill) => (
              <Chip
                key={skill}
                label={skill}
                size="small"
                onDelete={() => {}}
                sx={{
                  height: 24,
                  borderRadius: "999px",
                  fontSize: 11,
                  fontWeight: 500,
                  ...CHIP_COLORS.skills,
                  border: `1px solid ${CHIP_COLORS.skills.borderColor}`,
                  "& .MuiChip-deleteIcon": {
                    fontSize: 14,
                    color: CHIP_COLORS.skills.color,
                  },
                }}
              />
            ))}
          </Stack>
          <Typography
            component="button"
            type="button"
            sx={{
              mt: 1.2,
              border: 0,
              background: "transparent",
              p: 0,
              fontSize: 12,
              color: TAB_PURPLE,
              cursor: "pointer",
            }}
          >
            Add New
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const formatManagerNames = (value) =>
  String(value || "")
    .split(/[,/]| and /i)
    .map((item) => item.trim())
    .filter(Boolean);

function HiringManagerCell({ row = {} }) {
  const explicitNames = formatManagerNames(getDistinctJobHiringManagerName(row));
  const names = explicitNames;

  if (!names.length) {
    return (
      <Typography
        sx={{
          fontSize: 12,
          color: TAB_PURPLE,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Add new
      </Typography>
    );
  }

  if (names.length === 1) {
    return <Typography sx={{ fontSize: 12, color: "#475467" }}>{names[0]}</Typography>;
  }

  return (
    <Stack direction="row" spacing={-0.55} alignItems="center">
      {names.slice(0, 3).map((name, index) => (
        <Avatar
          key={`${name}-${index}`}
          sx={{
            width: 23,
            height: 23,
            fontSize: 10,
            bgcolor: ["#E9D5FF", "#DBEAFE", "#FDE68A"][index % 3],
            color: "#4B5563",
            border: "2px solid #FFFFFF",
          }}
        >
          {name.slice(0, 1).toUpperCase()}
        </Avatar>
      ))}
      {names.length > 3 ? (
        <Avatar
          sx={{
            width: 23,
            height: 23,
            fontSize: 11,
            bgcolor: "#FFFFFF",
            color: TAB_PURPLE,
            border: "1px solid #D8B4FE",
            ml: 0.75,
          }}
        >
          +
        </Avatar>
      ) : null}
    </Stack>
  );
}

export default function CompanyJobsTabContent({
  company = {},
  leadId,
  companyId,
  reloadKey = 0,
  onJobsChange,
  onAddNewJob,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams?.() || {};
  const routeCompanyId = new URLSearchParams(location.search).get("companyId");
  const pathCompanyId = params?.companyId ?? params?.leadId;

  const resolvedCompanyId = useMemo(() => {
    const rawValue =
      routeCompanyId ??
      companyId ??
      company?.company_id ??
      company?.companyId ??
      company?.id ??
      pathCompanyId ??
      leadId;
    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : NaN;
  }, [
    companyId,
    leadId,
    company?.company_id,
    company?.companyId,
    company?.id,
    pathCompanyId,
    routeCompanyId,
  ]);

  const isValidCompanyId = Number.isFinite(resolvedCompanyId) && resolvedCompanyId > 0;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [jobs, setJobs] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const fetchJobs = useCallback(async () => {
    if (!isValidCompanyId) {
      setJobs([]);
      setSelectedIds(new Set());
      setError("");
      onJobsChange?.({ rows: [], total: 0 });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE}/api/jobs?page=1&limit=100&sort=created_at&order=desc&companyId=${encodeURIComponent(
          String(resolvedCompanyId)
        )}`,
        {
          headers: { ...getAuthHeaders() },
          credentials: "include",
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to load company jobs (${response.status})`);
      }

      const nextJobs = Array.isArray(payload?.rows) ? payload.rows : [];
      setJobs(nextJobs);
      setSelectedIds(new Set());
      onJobsChange?.({
        rows: nextJobs,
        total: Number(payload?.total || nextJobs.length),
        companyId: resolvedCompanyId,
      });
    } catch (fetchError) {
      console.error("Failed to load company jobs:", fetchError);
      setJobs([]);
      setSelectedIds(new Set());
      setError(fetchError?.message || "Unable to load company jobs.");
    } finally {
      setLoading(false);
    }
  }, [isValidCompanyId, onJobsChange, resolvedCompanyId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs, reloadKey]);

  const visibleJobs = jobs;

  const allChecked =
    visibleJobs.length > 0 &&
    visibleJobs.every((row) => selectedIds.has(String(row?.id)));
  const someChecked = selectedIds.size > 0 && !allChecked;

  const toggleAllVisible = useCallback(
    (checked) => {
      if (!checked) {
        setSelectedIds(new Set());
        return;
      }
      setSelectedIds(new Set(visibleJobs.map((row) => String(row?.id))));
    },
    [visibleJobs]
  );

  const deleteJobsByIds = useCallback(async (ids = []) => {
    const safeIds = ids
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!safeIds.length) return;

    const response = await fetch(`${API_BASE}/api/jobs/bulk-delete`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify({ ids: safeIds }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error || "Failed to delete jobs.");
    }
    notifyCompanyRelationshipsChanged();
  }, []);

  const deleteOne = useCallback(
    async (id) => {
      if (!id) return;

      try {
        await deleteJobsByIds([id]);
        await fetchJobs();
      } catch (deleteError) {
        console.error("Failed to delete company job:", deleteError);
      }
    },
    [deleteJobsByIds, fetchJobs]
  );

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;

    try {
      await deleteJobsByIds(ids);
      await fetchJobs();
    } catch (deleteError) {
      console.error("Failed to bulk delete company jobs:", deleteError);
    }
  }, [deleteJobsByIds, fetchJobs, selectedIds]);

  const handleBulkStatusChange = useCallback(
    async (status) => {
      const nextStatus = String(status || "").trim();
      if (!nextStatus) return;

      const ids = Array.from(selectedIds)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!ids.length) return;

      try {
        const response = await fetch(`${API_BASE}/api/jobs/bulk-status`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({ ids, status: nextStatus }),
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Failed to update company job statuses.");
        }

        const idSet = new Set(ids.map((value) => String(value)));
        setJobs((prev) =>
          prev.map((row) => (idSet.has(String(row?.id)) ? { ...row, status: nextStatus } : row))
        );
        setStatusToast({
          open: true,
          severity: "success",
          message: `Updated ${ids.length} job${ids.length === 1 ? "" : "s"} to "${nextStatus}".`,
        });
      } catch (statusError) {
        console.error("Failed to update company job statuses:", statusError);
        setStatusToast({
          open: true,
          severity: "error",
          message: statusError?.message || "Failed to update company job statuses.",
        });
      }
    },
    [selectedIds]
  );

  const selectedJobs = useMemo(
    () => visibleJobs.filter((row) => selectedIds.has(String(row?.id))),
    [visibleJobs, selectedIds]
  );

  const selectedSequenceItems = useMemo(
    () => selectedJobs.map((row) => buildJobSequenceItem(row, "company-jobs")),
    [selectedJobs]
  );

  const tableColumns = useMemo(
    () => [
      {
        id: "_select",
        label: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              pl: `${TABLE_SELECTION_HEADER_INSET_PX}px`,
            }}
          >
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={(event) => toggleAllVisible(event.target.checked)}
              sx={{
                p: 0,
                width: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                height: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                minWidth: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                minHeight: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "& .MuiSvgIcon-root": { fontSize: TABLE_SELECTION_CHECKBOX_SIZE_PX },
              }}
            />
          </Box>
        ),
        numeric: false,
        sortable: false,
        width: TABLE_SELECTION_COLUMN_WIDTH_PX,
        minWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
        maxWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
      },
      { id: "job_title", label: "Job Title", numeric: false },
      { id: "status", label: "Status", numeric: false },
      { id: "_hm", label: "Hiring Manager", numeric: false },
      { id: "source", label: "Source", numeric: false },
      { id: "candidates_count", label: "Applications", numeric: true },
      { id: "best_match_percentage", label: "% of Best Matches", numeric: false },
      { id: "_actions", label: "", numeric: false },
    ],
    [allChecked, someChecked, toggleAllVisible]
  );

  const openJobDetails = useCallback(
    (row, tab = "details") => {
      const jobId = Number(row?.id || 0);
      if (!Number.isFinite(jobId) || jobId <= 0) return;

      const normalizedCompanyId = Number(
        company?.company_id ?? company?.companyId ?? company?.id ?? resolvedCompanyId
      );
      const nextCompanyId =
        Number.isFinite(normalizedCompanyId) && normalizedCompanyId > 0
          ? normalizedCompanyId
          : "";
      const parentEntityType =
        String(company?.entityType || "").toLowerCase() === "company" ? "company" : "lead";
      const returnListTab = parentEntityType === "company" ? "companies" : "clients";
      const parentCompany = buildOpenCompanyNavEntity(company, {
        entityType: parentEntityType,
      });
      if (!parentCompany?.id) return;

      navigate(
        {
          pathname: "/jobs",
          search: `?jobId=${encodeURIComponent(jobId)}&tab=${encodeURIComponent(
            String(tab || "details").toLowerCase() || "details"
          )}${nextCompanyId ? `&companyId=${encodeURIComponent(nextCompanyId)}` : ""}`,
        },
        {
          replace: false,
          state: {
            companyOrigin: {
              parentCompany,
              parentTab: "jobs",
              returnListTab,
            },
          },
        }
      );
    },
    [company, navigate, resolvedCompanyId]
  );

  const renderJobRow = useCallback(
    (row) => {
      const applicationCount = Number(
        row?.matches_count ??
          row?.matchesCount ??
          row?.candidates_count ??
          row?.applications_count ??
          0
      );

      return (
        <>
          <TableCell
            sx={{
              width: TABLE_SELECTION_COLUMN_WIDTH_PX,
              minWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
              maxWidth: TABLE_SELECTION_COLUMN_WIDTH_PX,
              pl: `${TABLE_SELECTION_COLUMN_INSET_PX}px`,
              pr: 0,
              py: 1.45,
              verticalAlign: "middle",
            }}
          >
            <Checkbox
              checked={selectedIds.has(String(row?.id))}
              onChange={(event) => {
                const checked = event.target.checked;
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (checked) next.add(String(row?.id));
                  else next.delete(String(row?.id));
                  return next;
                });
              }}
              onClick={(event) => event.stopPropagation()}
              sx={{
                p: 0,
                width: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                height: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                minWidth: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                minHeight: TABLE_SELECTION_CHECKBOX_SIZE_PX,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "& .MuiSvgIcon-root": { fontSize: TABLE_SELECTION_CHECKBOX_SIZE_PX },
              }}
            />
          </TableCell>

          <TableCell>
            <Typography
              sx={{ fontSize: 12, color: "#475467", cursor: "pointer", fontWeight: 500 }}
              onClick={(event) => {
                event.stopPropagation();
                openJobDetails(row);
              }}
            >
              {row?.job_title || "-"}
            </Typography>
          </TableCell>

          <TableCell>
            <Chip
              label={row?.status || "-"}
              size="small"
              sx={{
                height: 20,
                borderRadius: "999px",
                fontWeight: 500,
                fontSize: 10,
                px: 0.25,
                ...statusChipSx(row?.status),
              }}
            />
          </TableCell>

          <TableCell>
            <HiringManagerCell row={row} />
          </TableCell>

          <TableCell>
            <Typography sx={{ fontSize: 12, color: "#475467" }}>
              {row?.source || "-"}
            </Typography>
          </TableCell>

          <TableCell>
            <Typography sx={{ fontSize: 12, color: "#475467" }}>
              {applicationCount}
            </Typography>
          </TableCell>

          <TableCell sx={{ minWidth: 165 }}>
            {row?.best_match_percentage === null ||
            row?.best_match_percentage === undefined ||
            row?.best_match_percentage === "" ? (
              <Typography sx={{ fontSize: 12, color: "#98A2B3" }}>-</Typography>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    flex: 1,
                    height: 7,
                    borderRadius: 999,
                    bgcolor: "#F2F4F7",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${Math.min(100, Math.max(0, Number(row.best_match_percentage)))}%`,
                      borderRadius: 999,
                      bgcolor: "#7F56D9",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#344054",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {Number(row.best_match_percentage)}%
                </Typography>
              </Box>
            )}
          </TableCell>

          <TableCell sx={{ width: 48, pr: 1 }}>
            <RowKebab
              onDelete={() => deleteOne(row.id)}
              onMenuOpen={() => setMenuOpen(true)}
              onMenuClose={() => setMenuOpen(false)}
            />
          </TableCell>
        </>
      );
    },
    [deleteOne, openJobDetails, selectedIds]
  );

  const getJobRowProps = useCallback(
    (row) => {
      const jobId = Number(row?.id || 0);
      if (!Number.isFinite(jobId) || jobId <= 0) return undefined;

      return {
        // A click used to dismiss a portalled row menu bubbles through this
        // row in React. Treat it as a menu dismissal, not a request to open
        // the job details page.
        onClick: () => {
          if (!menuOpen) openJobDetails(row);
        },
        sx: {
          cursor: "pointer",
        },
      };
    },
    [menuOpen, openJobDetails]
  );

  const hasJobs = jobs.length > 0;
  return (
    <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
      <CompanyTabSectionHeader
        title="Jobs"
        actions={[
          { label: "Add New", onClick: onAddNewJob },
          ...(selectedJobs.length === 1
            ? [{ label: "Edit", onClick: () => openJobDetails(selectedJobs[0]) }]
            : []),
        ]}
      />

      {error ? (
        <Typography sx={{ mb: 2, fontSize: 13, color: "#B45309" }}>{error}</Typography>
      ) : null}

      <Box sx={{ backgroundColor: "#fff" }}>
        <BulkActionsBar
          selectedCount={selectedIds.size}
          onAddToSequence={() => setIsAddToSequenceOpen(true)}
          onBulkDelete={bulkDelete}
          onChangeStatus={handleBulkStatusChange}
          statusFeedback={statusToast}
          onStatusFeedbackClose={() =>
            setStatusToast((prev) => ({ ...prev, open: false }))
          }
        />

        {loading && !hasJobs ? (
          <Box
            sx={{
              py: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : !hasJobs ? (
          <Box
            sx={{
              borderRadius: "14px",
              border: "1.5px dashed #D1D5DB",
              mt: 2,
              py: 6,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#6B7280", fontWeight: 500 }}>
              No jobs are linked to this company yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ borderTop: "1px solid #E5E7EB", borderBottom: "1px solid #E5E7EB" }}>
            <GenericDataTable
              columns={tableColumns}
              rows={visibleJobs}
              renderRow={renderJobRow}
              getRowProps={getJobRowProps}
              bulkActionsTop={null}
              bulkActionsBottom={null}
              defaultOrderBy="created_at"
              defaultOrderDirection="desc"
              includeSelectionColumn={false}
              initialRowsPerPage={100}
              showResultsHeader={false}
              paginationBarSx={{ display: "none" }}
              loading={loading}
            />
          </Box>
        )}

        {hasJobs ? (
          <BulkActionsBar
            selectedCount={selectedIds.size}
            onAddToSequence={() => setIsAddToSequenceOpen(true)}
            onBulkDelete={bulkDelete}
            onChangeStatus={handleBulkStatusChange}
            statusFeedback={statusToast}
            onStatusFeedbackClose={() =>
              setStatusToast((prev) => ({ ...prev, open: false }))
            }
          />
        ) : null}
      </Box>

      <AddToSequenceDialog
        open={isAddToSequenceOpen}
        onClose={() => setIsAddToSequenceOpen(false)}
        entityType="job"
        items={selectedSequenceItems}
        onCreated={() => setSelectedIds(new Set())}
      />

      <PreferencesSection company={company} />

    </Box>
  );
}
